import React from 'react';
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CalendarOrderControls } from './CalendarOrderControls';

/** Above the other rows' sticky technician column (z-30), below the sticky header (z-40). */
const DRAGGED_Z = 35;

interface ListProps {
  /** Technician ids in the order they are drawn. */
  ids: number[];
  editing: boolean;
  onMove: (activeId: number, overId: number) => void;
  children: React.ReactNode;
}

/**
 * Makes the rows below it sortable while `editing`, and is a no-op wrapper otherwise.
 *
 * Vertical only, and the rows themselves travel (no overlay) — the sticky technician column and the
 * track are one element, so the whole row reads as being picked up.
 */
export const CalendarOrderList = ({ ids, editing, onMove, children }: ListProps) => {
  const sensors = useSensors(
    // A few pixels of travel before a drag starts, so a click on the handle does not become one.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  if (!editing) return <>{children}</>;

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (over && active.id !== over.id) onMove(Number(active.id), Number(over.id));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {children}
      </SortableContext>
    </DndContext>
  );
};

interface RowProps {
  technicianId: number;
  editing: boolean;
  hidden: boolean;
  onToggleHidden: (technicianId: number) => void;
  className?: string;
  style?: React.CSSProperties;
  /** `controls` is the handle + eye pair for `TechnicianInfo`'s `actions` slot; `undefined` outside edit mode. */
  children: (controls: React.ReactNode | undefined) => React.ReactNode;
}

/** One row of the list: the frame that moves, and the controls that move it. */
export const CalendarOrderRow = (props: RowProps) => {
  const { editing, className, style, children } = props;

  if (!editing) {
    return (
      <div className={className} style={style}>
        {children(undefined)}
      </div>
    );
  }

  return <SortableFrame {...props} />;
};

const SortableFrame = ({
  technicianId,
  hidden,
  onToggleHidden,
  className,
  style,
  children,
}: RowProps) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({
      id: technicianId,
      // A Handwerker slides into place after being hidden or shown, not only after a drop.
      animateLayoutChanges: () => true,
    });

  const frameStyle: React.CSSProperties = {
    ...style,
    transform: transform ? `translate3d(0, ${Math.round(transform.y)}px, 0)` : undefined,
    transition,
    position: 'relative',
    zIndex: isDragging ? DRAGGED_Z : undefined,
    boxShadow: isDragging ? '0 8px 24px rgba(15, 23, 42, 0.18)' : undefined,
  };

  return (
    <div ref={setNodeRef} className={className} style={frameStyle}>
      {children(
        <CalendarOrderControls
          hidden={hidden}
          dragging={isDragging}
          handleRef={setActivatorNodeRef}
          handleProps={{ ...attributes, ...listeners }}
          onToggleHidden={() => onToggleHidden(technicianId)}
        />,
      )}
    </div>
  );
};
